import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Shield, Bug, RefreshCw, Skull, Globe, Server, ExternalLink,
  AlertOctagon, Activity, Maximize2, Minimize2, KeyRound, Copy, ShieldOff,
} from 'lucide-react'
import { defangUrl, defangIp, defangIoc, copyRefanged } from '../lib/defang'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'
import ActiveWarRoom from '../components/ActiveWarRoom'
import TTEHeadline from '../components/TTEHeadline'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

const SEV: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  critical: { bg: 'bg-rose/10',    text: 'text-rose',        border: 'border-rose/30',    dot: 'bg-rose' },
  high:     { bg: 'bg-orange/10',  text: 'text-orange',      border: 'border-orange/30',  dot: 'bg-orange' },
  medium:   { bg: 'bg-amber/10',   text: 'text-amber-light', border: 'border-amber/30',   dot: 'bg-amber' },
  low:      { bg: 'bg-emerald/10', text: 'text-emerald',     border: 'border-emerald/30', dot: 'bg-emerald' },
  info:     { bg: 'bg-blue/10',    text: 'text-blue-light',  border: 'border-blue/30',    dot: 'bg-blue' },
}

// ── Panel wrapper: scrollable panel with header ──────────────────────────────
function Panel({
  title, count, icon: Icon, color, children, className,
}: {
  title: string
  count?: number
  icon: React.ElementType
  color: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={clsx('card flex flex-col min-h-0', className)}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border shrink-0">
        <h3 className="text-xs font-semibold text-text-primary flex items-center gap-2">
          <Icon size={13} className={color} />
          {title}
          {typeof count === 'number' && (
            <span className="text-[10px] text-text-muted font-normal">{count}</span>
          )}
        </h3>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function CyberWarRoomPage() {
  const [data, setData] = useState<any>(null)
  const [threatLevel, setThreatLevel] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [fullscreen, setFullscreen] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now())

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [liveRes, threatRes] = await Promise.allSettled([
        api.get('/api/threats/live', { params: { limit: 200 } }),
        api.get('/api/cyber/threat-level'),
      ])
      if (liveRes.status === 'fulfilled') setData(liveRes.value.data)
      if (threatRes.status === 'fulfilled') setThreatLevel(threatRes.value.data)
      setLastUpdated(Date.now())
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    fetchData()
    const i = setInterval(fetchData, 60_000)
    return () => clearInterval(i)
  }, [fetchData])

  // Fullscreen key toggle (F)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
        setFullscreen(v => !v)
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])

  const stats = data?.stats || {}
  const rawAdvisories: any[] = data?.advisories || []
  const rawIocs: any[] = data?.iocs || []
  const rawUrls: any[] = data?.malicious_urls || []
  const rawC2s: any[] = data?.c2_servers || []

  // Sort: severity first (critical > high > medium > low > info), then newest first.
  const SEV_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 }
  const advisories = useMemo(
    () => [...rawAdvisories].sort((a, b) => {
      const sa = SEV_RANK[a.severity] ?? 5
      const sb = SEV_RANK[b.severity] ?? 5
      if (sa !== sb) return sa - sb
      const ta = new Date(a.timestamp || 0).getTime()
      const tb = new Date(b.timestamp || 0).getTime()
      return tb - ta
    }),
    [rawAdvisories]
  )

  // IOCs: highest confidence first, then newest
  const iocs = useMemo(
    () => [...rawIocs].sort((a, b) => {
      const ca = a.confidence ?? 0
      const cb = b.confidence ?? 0
      if (ca !== cb) return cb - ca
      const ta = new Date(a.first_seen || 0).getTime()
      const tb = new Date(b.first_seen || 0).getTime()
      return tb - ta
    }),
    [rawIocs]
  )

  // Malicious URLs: online first, then newest
  const urls = useMemo(
    () => [...rawUrls].sort((a, b) => {
      const oa = a.status === 'online' ? 0 : 1
      const ob = b.status === 'online' ? 0 : 1
      if (oa !== ob) return oa - ob
      const ta = new Date(a.date_added || 0).getTime()
      const tb = new Date(b.date_added || 0).getTime()
      return tb - ta
    }),
    [rawUrls]
  )

  // C2s: online first, then most-recently-online first
  const c2s = useMemo(
    () => [...rawC2s].sort((a, b) => {
      const oa = a.status === 'online' ? 0 : 1
      const ob = b.status === 'online' ? 0 : 1
      if (oa !== ob) return oa - ob
      const ta = new Date(a.last_online || 0).getTime()
      const tb = new Date(b.last_online || 0).getTime()
      return tb - ta
    }),
    [rawC2s]
  )

  const critical = useMemo(
    () => advisories.filter(a => a.severity === 'critical').slice(0, 12),
    [advisories]
  )

  // Root container: fills viewport height; in fullscreen mode, hide everything else
  const rootClass = fullscreen
    ? 'fixed inset-0 bg-base z-50 flex flex-col gap-2 p-3 animate-fade-in'
    : 'flex flex-col gap-2 animate-fade-in h-[calc(100vh-120px)]'

  return (
    <div className={rootClass}>
      {/* Header row: title + KPIs + refresh */}
      <div className="flex items-center gap-3 shrink-0">
        <div className="shrink-0">
          <h1 className="text-lg font-bold text-text-primary flex items-center gap-2 leading-none">
            <Skull size={18} className="text-rose" /> Cyber Situation Room
            <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose/30 bg-rose/10 text-rose ml-1">
              <ShieldOff size={9} /> Defanged
            </span>
          </h1>
          <p className="text-[9px] text-text-secondary">
            {stats.sources_active || 0}/{stats.sources_total || 0} sources ·
            updated {relativeTime(new Date(lastUpdated).toISOString())} · auto-refresh 60s · press <kbd className="font-mono">F</kbd> for fullscreen · all indicators defanged (hxxp / [.]), copy icon to grab live
          </p>
        </div>

        {/* Compact KPI strip — flex, wraps */}
        <div className="flex-1 flex items-center gap-1.5 flex-wrap justify-end">
          <KPI
            label="Threat"
            value={(threatLevel?.level || '—').toUpperCase()}
            tone={threatLevel?.level === 'critical' ? 'rose' : threatLevel?.level === 'elevated' ? 'amber' : 'emerald'}
            big
          />
          <KPI label="Advisories" value={stats.total_advisories ?? advisories.length} tone="text" />
          <KPI
            label="Critical"
            value={critical.length}
            tone="rose"
            sparkline={<Sparkline metric="cves_kev_30d" hours={168} width={50} height={14} tone="down-good" />}
          />
          <KPI label="IOCs" value={stats.total_iocs ?? iocs.length} tone="text" />
          <KPI label="Bad URLs" value={stats.total_malicious_urls ?? urls.length} tone="orange" />
          <KPI
            label="C2s"
            value={stats.total_c2_servers ?? c2s.length}
            tone="rose"
            sparkline={<Sparkline metric="cves_kev" hours={168} width={50} height={14} tone="down-good" />}
          />
          <button type="button" onClick={fetchData} className="btn-ghost text-xs ml-1" title="Refresh" aria-label="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setFullscreen(v => !v)}
            className="btn-ghost text-xs"
            title={fullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}
            aria-label="Toggle fullscreen"
          >
            {fullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
        </div>
      </div>

      {/* Critical-advisory ticker */}
      {critical.length > 0 && (
        <div className="card px-3 py-1.5 border border-rose/30 bg-rose/5 shrink-0 overflow-hidden">
          <div className="flex items-center gap-2">
            <AlertOctagon size={14} className="text-rose shrink-0 animate-pulse" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-rose shrink-0">
              Critical
            </span>
            <div className="overflow-hidden whitespace-nowrap flex-1 relative">
              <div className="inline-flex gap-6 animate-marquee">
                {[...critical, ...critical].map((a, i) => (
                  <a
                    key={i}
                    href={a.url || '#'}
                    target={a.url ? '_blank' : undefined}
                    rel="noopener"
                    className="text-[11px] text-rose-light hover:text-rose hover:underline shrink-0"
                  >
                    <span className="font-semibold">{a.source}:</span> {a.title?.slice(0, 120)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Macro time-to-exploit headline — frames the posture in velocity terms */}
      <div className="shrink-0"><TTEHeadline compact /></div>

      {/* Active Situation Room banner — open incidents + KEV velocity + cable faults */}
      <ActiveWarRoom />

      {/* Main grid — advisories left, IOCs + C2s middle, URLs right */}
      <div className="flex-1 min-h-0 grid gap-2 grid-cols-1 lg:grid-cols-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.9fr)]">

        {/* LEFT: Advisories feed */}
        <Panel title="Advisories" count={advisories.length} icon={Shield} color="text-blue">
          <div className="divide-y divide-border/50">
            {advisories.map((a: any, i: number) => {
              const s = SEV[a.severity] || SEV.info
              return (
                <div key={i} className="px-3 py-2 hover:bg-surface-raised/30 transition-colors">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', s.dot)} />
                    <span className={clsx('text-[9px] font-semibold uppercase px-1 py-0.5 rounded border', s.bg, s.text, s.border)}>
                      {a.severity}
                    </span>
                    <span className="text-[9px] text-text-muted truncate">{a.source}</span>
                    <span className="text-[9px] text-text-muted ml-auto shrink-0">{relativeTime(a.timestamp)}</span>
                  </div>
                  <a
                    href={a.url || '#'}
                    target={a.url ? '_blank' : undefined}
                    rel="noopener"
                    className="text-[11px] font-medium text-text-primary leading-snug hover:text-blue-light hover:underline block"
                  >
                    {a.title}
                  </a>
                  {a.summary && (
                    <p className="text-[10px] text-text-muted mt-0.5 line-clamp-2">{a.summary}</p>
                  )}
                </div>
              )
            })}
            {advisories.length === 0 && (
              <div className="p-4 text-center text-[11px] text-text-muted">No advisories loaded</div>
            )}
          </div>
        </Panel>

        {/* MIDDLE: IOCs on top, C2s on bottom */}
        <div className="flex flex-col gap-2 min-h-0">
          <Panel title="Live IOCs" count={iocs.length} icon={Bug} color="text-rose" className="flex-[1.3] min-h-0">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-surface-card z-10">
                <tr className="text-left text-text-muted border-b border-border">
                  <th className="py-1 px-2 font-semibold">IOC</th>
                  <th className="py-1 px-2 font-semibold">Type</th>
                  <th className="py-1 px-2 font-semibold">Malware</th>
                  <th className="py-1 px-2 font-semibold text-right">Conf</th>
                  <th className="py-1 px-2 font-semibold text-right">Seen</th>
                </tr>
              </thead>
              <tbody>
                {iocs.map((ioc: any, i: number) => {
                  const defanged = defangIoc(ioc.ioc, ioc.ioc_type)
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-surface-raised/30">
                      <td className="py-1 px-2 font-mono text-rose-light truncate max-w-[180px]" title={`DEFANGED — click icon to copy live: ${defanged}`}>
                        <span className="inline-flex items-center gap-1">
                          <button type="button" onClick={() => copyRefanged(defanged)} className="text-text-muted hover:text-rose" title="Copy live (un-defanged) — use with caution" aria-label="Copy live indicator">
                            <Copy size={9} />
                          </button>
                          <span className="truncate">{defanged}</span>
                        </span>
                      </td>
                      <td className="py-1 px-2 text-text-muted whitespace-nowrap">{ioc.ioc_type}</td>
                      <td className="py-1 px-2 text-amber-light font-medium whitespace-nowrap">{ioc.malware}</td>
                      <td className="py-1 px-2 text-right">
                        <span className={clsx('font-mono font-bold', ioc.confidence >= 75 ? 'text-rose' : ioc.confidence >= 50 ? 'text-amber' : 'text-text-muted')}>
                          {ioc.confidence}%
                        </span>
                      </td>
                      <td className="py-1 px-2 text-text-muted whitespace-nowrap text-right font-mono">{(ioc.first_seen || '').slice(5, 16).replace('T', ' ')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {iocs.length === 0 && (
              <div className="p-4 text-center text-[11px] text-text-muted">
                <KeyRound size={14} className="mx-auto mb-1 text-amber" />
                ThreatFox needs an Auth-Key.<br />
                Free at <a href="https://auth.abuse.ch" target="_blank" rel="noopener" className="text-blue-light hover:underline">auth.abuse.ch</a>, then set <code className="font-mono">ABUSE_CH_AUTH_KEY</code> in backend/.env
              </div>
            )}
          </Panel>

          <Panel title="Botnet C2 Servers" count={c2s.length} icon={Server} color="text-rose" className="flex-1 min-h-0">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-surface-card z-10">
                <tr className="text-left text-text-muted border-b border-border">
                  <th className="py-1 px-2 font-semibold">IP</th>
                  <th className="py-1 px-2 font-semibold">Port</th>
                  <th className="py-1 px-2 font-semibold">Malware</th>
                  <th className="py-1 px-2 font-semibold">Status</th>
                  <th className="py-1 px-2 font-semibold text-right">Last</th>
                </tr>
              </thead>
              <tbody>
                {c2s.map((c: any, i: number) => {
                  const defanged = defangIp(c.ip)
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-surface-raised/30">
                      <td className="py-1 px-2 font-mono text-rose-light whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <button type="button" onClick={() => copyRefanged(defanged)} className="text-text-muted hover:text-rose" title="Copy live IP" aria-label="Copy live IP">
                            <Copy size={9} />
                          </button>
                          {defanged}
                        </span>
                      </td>
                      <td className="py-1 px-2 text-text-muted font-mono">{c.port}</td>
                      <td className="py-1 px-2 text-amber-light whitespace-nowrap">{c.malware}</td>
                      <td className="py-1 px-2">
                        <span className={clsx(c.status === 'online' ? 'text-rose font-bold' : 'text-text-muted')}>{c.status}</span>
                      </td>
                      <td className="py-1 px-2 text-text-muted whitespace-nowrap text-right font-mono">{(c.last_online || '').slice(5, 10)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {c2s.length === 0 && <div className="p-4 text-center text-[11px] text-text-muted">No C2 servers</div>}
          </Panel>
        </div>

        {/* RIGHT: Malicious URLs */}
        <Panel title="Malicious URLs" count={urls.length} icon={Globe} color="text-orange">
          <div className="divide-y divide-border/50">
            {urls.map((u: any, i: number) => {
              const defanged = defangUrl(u.url)
              return (
                <div key={i} className="px-3 py-1.5 hover:bg-surface-raised/30 transition-colors">
                  <div className="flex items-center gap-1.5">
                    <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', u.status === 'online' ? 'bg-rose animate-pulse' : 'bg-text-muted')} />
                    <span className="text-[9px] uppercase text-orange font-semibold shrink-0">{u.threat}</span>
                    <button
                      type="button"
                      onClick={() => copyRefanged(defanged)}
                      className="text-text-muted hover:text-rose"
                      title="Copy live URL (use with caution)"
                      aria-label="Copy live URL"
                    >
                      <Copy size={9} />
                    </button>
                    <span className="text-[9px] text-text-muted ml-auto shrink-0">{u.status}</span>
                  </div>
                  <div className="font-mono text-[10px] text-rose-light truncate mt-0.5" title={`DEFANGED — copy to use: ${defanged}`}>
                    {defanged}
                  </div>
                </div>
              )
            })}
            {urls.length === 0 && (
              <div className="p-4 text-center text-[11px] text-text-muted">
                <KeyRound size={14} className="mx-auto mb-1 text-amber" />
                URLhaus needs an Auth-Key.<br />
                Free at <a href="https://auth.abuse.ch" target="_blank" rel="noopener" className="text-blue-light hover:underline">auth.abuse.ch</a>, then set <code className="font-mono">ABUSE_CH_AUTH_KEY</code> in backend/.env
              </div>
            )}
          </div>
        </Panel>
      </div>

      {/* Footer sources — only when not fullscreen */}
      {!fullscreen && (
        <div className="text-[9px] text-text-muted text-center shrink-0 leading-relaxed">
          Sources: NCSC UK · NCSC NL · CISA · CERT-EU · CERT-FR · BSI · ACSC · CCCS ·
          ThreatFox · URLhaus · Feodo Tracker (abuse.ch) · Cisco Talos · Unit 42 · Mandiant ·
          CrowdStrike · SentinelOne · Elastic · Check Point · Trend Micro · Fortinet · MS Security ·
          Google TAG · MITRE ATT&CK · Shadowserver · SANS ISC
        </div>
      )}
    </div>
  )
}

// ── Compact KPI tile ─────────────────────────────────────────────────────────
function KPI({
  label, value, tone, big, sparkline,
}: {
  label: string
  value: React.ReactNode
  tone: 'rose' | 'orange' | 'amber' | 'emerald' | 'text'
  big?: boolean
  sparkline?: React.ReactNode
}) {
  const bg = {
    rose:    'bg-rose/10 border-rose/30 text-rose',
    orange:  'bg-orange/10 border-orange/30 text-orange',
    amber:   'bg-amber/10 border-amber/30 text-amber-light',
    emerald: 'bg-emerald/10 border-emerald/30 text-emerald',
    text:    'bg-surface-card border-border text-text-primary',
  }[tone]
  return (
    <div className={clsx('px-2.5 py-1 rounded border flex items-center gap-2 shrink-0', bg)}>
      <span className="text-[9px] uppercase tracking-wider text-text-muted font-semibold">{label}</span>
      <span className={clsx('font-mono font-bold', big ? 'text-sm' : 'text-xs')}>{value}</span>
      {sparkline}
    </div>
  )
}
